import "./sales-landing.css";
import { useEffect, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";

declare global {
  interface Window {
    fpr?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      queue?: unknown[];
      callMethod?: unknown;
      loaded?: boolean;
      version?: string;
      push?: unknown;
    };
    _fbq?: unknown;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    ttq?: {
      track: (name: string, params?: Record<string, unknown>) => void;
      load: (id: string) => void;
      page: () => void;
    };
  }
}

// ⚠️ KONFIGURACJA TRACKINGU — bez tego Meta/Google/TikTok nie zoptymalizują CPA
//   Wstaw realne ID przed puszczeniem cold trafficu. Bez nich strona działa, ale ads są ślepe.
const META_PIXEL_ID: string | null = null; // np. '123456789012345'
const GA4_ID: string | null = null; // np. 'G-XXXXXXXXXX'
const TIKTOK_PIXEL_ID: string | null = null; // np. 'CXXXXXXXXX'

// ⚠️ LEAD MAGNET — submit emaila → POST na ten webhook (n8n / Zapier / Make / własny endpoint)
//   Webhook dostaje JSON: { email, utm_source, utm_campaign, ..., page: '/lp/ads', timestamp }
//   Jak null → cała sekcja ukryta. Po podpięciu webhooka — odpalasz cold traffic na "darmowy PDF"
//   z 3-4× wyższą capture rate niż na sam trial.
const LEAD_WEBHOOK_URL: string | null = null; // np. 'https://hooks.n8n.io/abc123'
const LEAD_MAGNET_PDF_URL: string | null = null; // URL do PDF który user dostaje po sign-upie (opcjonalnie)

function injectMetaPixel(id: string) {
  if (window.fbq) return;
  /* eslint-disable */
  (function (f: any, b: any, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e);
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window as any, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  (window as any).fbq?.("init", id);
  (window as any).fbq?.("track", "PageView");
}

function injectGA4(id: string) {
  if (window.gtag) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  (window as any).gtag = function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag!("js", new Date());
  window.gtag!("config", id);
}

function injectTikTokPixel(id: string) {
  if (window.ttq) return;
  /* eslint-disable */
  // @ts-ignore -- standard TikTok pixel snippet
  !(function (w: any, d: any, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
    ];
    ttq.setAndDefer = function (t: any, e: any) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t: any) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e: any, n: any) {
      const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = d.createElement("script");
      o.type = "text/javascript";
      o.async = true;
      o.src = r + "?sdkid=" + e + "&lib=" + t;
      const a = d.getElementsByTagName("script")[0];
      a.parentNode.insertBefore(o, a);
    };
    ttq.load(id);
    ttq.page();
  })(window, document, "ttq");
  /* eslint-enable */
}

function trackConversion(eventName: string, params?: Record<string, unknown>) {
  try {
    window.fbq?.("track", eventName, params);
    window.gtag?.("event", eventName, params);
    window.ttq?.track(eventName, params);
  } catch (e) {
    /* swallow */
  }
}

// ─── Constants ───
const VP_LOGO =
  "https://assets.cdn.filesafe.space/bVblz6CWD31c4iY9FJQr/media/69cb85210c5c89a989cb4afc.png";

// ⚠️ Realne kreatywy wygenerowane w Ad Creator-ze. Podmień URL-e na te z CDN/Supabase storage.
//    Format: 1080×1350 (4:5) lub 1080×1080 (1:1) — zoptymalizowane pod feed Meta/IG.
const AD_CREATIVES: Array<{ src: string; alt: string; caption: string; tag: string }> = [
  {
    src: "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/WhatsApp+Image+2026-05-02+at+19.29.22.jpeg",
    alt: "Reklama usług — pozbądź się bólu pleców",
    caption: "Reklama usług → first call",
    tag: "Lead → konsultacja",
  },
  {
    src: "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/reklama-feed-v2+(4).png",
    alt: "Reklama e-commerce — okulary",
    caption: "Reklama produktu e-commerce",
    tag: "E-commerce",
  },
  {
    src: "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/reklama-square-v2+(2).png",
    alt: "Reklama e-commerce — perfumy",
    caption: "Reklama produktu e-commerce",
    tag: "E-commerce",
  },
  {
    src: "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/dom+w+bieszczadach.png",
    alt: "Reklama nieruchomości — Bieszczady",
    caption: "Reklama nieruchomości — lead generation",
    tag: "Lead gen",
  },
];

// ⚠️ ZAMIEŃ NA REALNY ASSET ZANIM PUŚCISZ COLD TRAFFIC
//   Opcja A: screenshot dashboardu — ustaw HERO_IMAGE_URL na adres .png/.webp (1600x1200)
//   Opcja B: 30–60s demo video — ustaw HERO_VIDEO_URL na .mp4 (autoplay muted loop)
//   Jak null/empty → pokazuje CSS-mock fallback
const HERO_IMAGE_URL: string | null = null;
const HERO_VIDEO_URL: string | null = null;

// SEO + OG meta — używane przez Googlebot (renderuje JS) oraz tab title.
// ⚠️ Dla full FB/LinkedIn/Twitter sharing trzeba SSR injection w serverze (out of scope iter 8).
//    Dziś Meta crawler nie zrenderuje JS — share preview pokaże globalny "Viral Partners | MarketingOS".
const SEO_CONFIG = {
  title: "Twoje reklamy palą budżet — bo nie wiesz co konwertuje | Viral Partners",
  description:
    "Polski system tracking + AI kreacje (grafiki, video, copy) + analityka. Zastępuje Hyros + Canva + ChatGPT + edytora video. Umów demo i zobacz jak to działa na żywo.",
  canonical: "https://viral.partners/lp/ads",
  ogImage: "https://viral.partners/og/lp-ads-1200x630.png", // ⚠️ stwórz i wstaw realny URL
};
const TOP_AD_SPEND_COUNT = 3;
const PLN_UNIT = " zł";

interface AdSpendEntry {
  firstName: string;
  totalSpend: number;
  currency: string;
}
interface AdSpendStats {
  entries: AdSpendEntry[];
  totalManaged: number;
  userCount: number;
  fetchedAt: string;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "ttclid",
];

function passthroughParams(): string {
  const sp = new URLSearchParams(window.location.search);
  const out = new URLSearchParams();
  for (const k of UTM_KEYS) {
    const v = sp.get(k);
    if (v) out.set(k, v);
  }
  const fpr = sp.get("fpr");
  if (fpr) out.set("fpr", fpr);
  const s = out.toString();
  return s ? `?${s}` : "";
}

function buildAppUrl(): string {
  return `https://app.viral.partners${passthroughParams()}`;
}
// "Umów demo" CTAs anchor-scroll to the on-page #demo section that embeds the
// GHL booking widget — no Calendly redirect (the calendly URL 404'd in prod).
function demoUrl(): string {
  return "#demo";
}
function formatPln(n: number): string {
  return Math.round(n).toLocaleString("pl-PL").replace(/,/g, " ") + PLN_UNIT;
}

// ═══════════════════════════════════════════════════════════════════════════
// TRUST MICRO (above-fold, under hero CTA — real numbers from public API)
// ═══════════════════════════════════════════════════════════════════════════
function TrustMicro() {
  return (
    <div className="trust-micro">
      <span>
        <strong>1 993 445 zł</strong> obsłużone przez naszą aplikację w ostatnich 30 dniach
      </span>
      <span>
        ★★★★★ <strong>polski i angielski support</strong>
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// URGENCY STRIP
// ═══════════════════════════════════════════════════════════════════════════
function UrgencyStrip() {
  return (
    <a href="#demo" className="urgency-strip">
      UMÓW <strong>PREZENTACJĘ</strong> NARZĘDZIA NA PRZYKŁADZIE <strong>TWOJEJ FIRMY!</strong>
    </a>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NAV
// ═══════════════════════════════════════════════════════════════════════════
function SalesNav() {
  return (
    <nav className="sl-nav">
      <div className="container">
        <a
          href="https://viral.partners/strona-gwna"
          className="sl-nav-logo"
          aria-label="Viral Partners"
        >
          <img src={VP_LOGO} alt="Viral Partners" />
        </a>
        <a href={demoUrl()} className="sl-nav-cta">
          Umów demo →
        </a>
      </div>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════════════
function Hero() {
  const variant =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("v") : null;
  const isB = variant === "b";

  const headlineA = (
    <>
      Twoje reklamy <span className="hl">palą budżet</span>
      <br />
      bo nie wiesz co naprawdę konwertuje.
    </>
  );
  const headlineB = (
    <>
      Twoi konkurenci <span className="hl">wiedzą</span> co konwertuje.
      <br />
      Ty zgadujesz.
    </>
  );
  const subA = (
    <>
      Viral Partners mówi Ci dokładnie <strong>która reklama sprowadza klienta</strong>, a{" "}
      <strong>która przepala pieniądze</strong> — i w 30 sekund generuje nowe kreacje (grafiki +
      video) razem z gotową, ustawioną kampanią reklamową. Zamiast 6 narzędzi — jedno.
    </>
  );
  const subB = (
    <>
      Tracking, który łapie sprzedaże niewidoczne w Meta Ads Manager. AI generator kreacji w stylu
      Twojej marki. Wszystko po polsku, w jednym panelu, za <strong>4× mniej</strong> niż Hyros +
      Canva + ChatGPT + edytor video razem.
    </>
  );

  return (
    <section className="hero hero-vsl-first">
      <div className="container">
        <div className="hero-vsl-stack">
          <span className="label">Dla firm, które inwestują w reklamy online</span>
          <h1>{isB ? headlineB : headlineA}</h1>
          <p className="hero-sub">{isB ? subB : subA}</p>

          <div className="hero-vsl-cta-pre">
            <span className="vsl-arrow">▼</span>
            Zobacz, jak Viral Partners znajduje wycieki w Twoich reklamach (20 min)
          </div>

          <HeroVSL />

          <div className="hero-cta-row hero-cta-row-center">
            <a href="#demo" className="btn btn-primary btn-xl">
              Umów 30-min demo
            </a>
            <a href="#pricing-tiers" className="btn btn-secondary btn-xl">
              Dołącz już teraz →
            </a>
          </div>
          <div className="hero-cta-meta hero-cta-meta-center">
            <span>Live na Twoim koncie</span>
            <span>30 minut</span>
            <span>Po polsku</span>
          </div>

          <ul className="hero-bullets hero-bullets-row">
            <li>Tracking łapie 30–50% sprzedaży niewidocznych w Meta Ads Manager</li>
            <li>AI generuje grafiki, video i copy w stylu Twojej marki (PL/EN)</li>
            <li>Meta, Google, TikTok, GoHighLevel, Shopify, HubSpot, Calendly</li>
            <li>Pierwszy raport tego samego dnia — zero pracy z developerem</li>
          </ul>

          <TrustMicro />
        </div>
      </div>
    </section>
  );
}

const WISTIA_MEDIA_ID = "thzculx2xq";

function HeroVSL() {
  useEffect(() => {
    const ensureScript = (id: string, src: string, asModule = false) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script");
      s.id = id;
      s.src = src;
      s.async = true;
      if (asModule) s.type = "module";
      document.head.appendChild(s);
    };
    ensureScript("wistia-player-loader", "https://fast.wistia.com/player.js");
    ensureScript(
      `wistia-media-${WISTIA_MEDIA_ID}`,
      `https://fast.wistia.com/embed/${WISTIA_MEDIA_ID}.js`,
      true
    );
  }, []);

  return (
    <div className="hero-vsl">
      {/* @ts-ignore — Wistia custom element */}
      <wistia-player media-id={WISTIA_MEDIA_ID} aspect="1.7777777777777777" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOGO / TRUST BAR
// ═══════════════════════════════════════════════════════════════════════════
function LogoBar() {
  const platforms = [
    { d: "facebook.com", a: "Meta" },
    { d: "tiktok.com", a: "TikTok" },
    { d: "shopify.com", a: "Shopify" },
    { d: "gohighlevel.com", a: "GoHighLevel" },
    { d: "hubspot.com", a: "HubSpot" },
    { d: "youtube.com", a: "YouTube" },
    { d: "instagram.com", a: "Instagram" },
  ];
  return (
    <section className="logo-bar">
      <div className="container">
        <div className="lbl">Działa z platformami, z których już korzystasz</div>
        <div className="logos">
          {platforms.map((p) => (
            <img
              key={p.d}
              src={`https://www.google.com/s2/favicons?domain=${p.d}&sz=128`}
              alt={p.a}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PROBLEM (agitation)
// ═══════════════════════════════════════════════════════════════════════════
function Problem() {
  const items = [
    {
      i: "📈",
      h: "Lejesz kasę w reklamy które nie sprzedają",
      p: 'E-commerce: Meta pokazuje 8 zakupów — w Shopify są 2. Lead gen: Meta pokazuje 10 leadów — w CRM jest 1 wartościowy. Ty podbijasz budżet 3×, bo "panel mówi że działa".',
    },
    {
      i: "🛑",
      h: "Wyłączasz reklamy które faktycznie sprzedają",
      p: "Reklama na zimny ruch: 0 zakupów / 0 leadów w panelu. Pauzujesz. A to ona dowiozła ludzi, którzy kupili tydzień później albo zostawili namiar handlowcowi. Zgasiłeś silnik kampanii.",
    },
    {
      i: "⚖️",
      h: "ROAS / CPL w Mecie ≠ rzeczywistość w kasie",
      p: "E-com: panel pokazuje ROAS 4.2 — po zwrotach i duplikatach masz 2.1. Lead gen: CPL 30 zł w Mecie, ale 70% leadów to spam. Realny koszt klienta: 4× wyższy.",
    },
  ];
  return (
    <section className="problem" id="problem">
      <div className="container">
        <div className="problem-header">
          <span className="label">Brutalna prawda</span>
          <h2>
            Zwiększasz budżet na reklamy, które <span className="strike">nie</span> sprzedają.
            <br />
            Wyłączasz te, które <span className="underline-accent">faktycznie</span> sprzedają.
          </h2>
          <p>
            Bo liczby w Ads Managerze rozjeżdżają się z tym, co masz w Shopify, CRM-ie albo arkuszu
            od handlowca — <strong>średnio o 30–50%</strong>. Podejmujesz decyzje za dziesiątki
            tysięcy miesięcznie na podstawie sprzedaży i leadów, których nie ma w realu.
          </p>
        </div>

        <DataLeakDiagram />

        <div className="problem-grid">
          {items.map((x) => (
            <div className="problem-card" key={x.h}>
              <div className="pc-icon">{x.i}</div>
              <h4>{x.h}</h4>
              <p>{x.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DataLeakDiagram() {
  const leaks = [
    {
      l: "Klika z telefonu, kupuje z laptopa",
      d: "Meta gubi go po drodze i przypisuje zakup ruchowi organicznemu",
    },
    {
      l: "Odrzuca cookies / iPhone pyta o zgodę",
      d: "~30% userów — Meta nie widzi co zrobili dalej",
    },
    { l: "Kupuje 10 dni po reklamie", d: "Meta liczy tylko 7 dni. Zakup ginie." },
    { l: "Zwrot po 3 dniach", d: "Meta ciągle pokazuje sprzedaż której nie ma" },
  ];
  return (
    <div className="data-leak">
      <div className="dl-row dl-source">
        <div className="dl-pill dl-platform">
          <span>Meta Ads</span>
        </div>
        <div className="dl-pill dl-platform">
          <span>TikTok Ads</span>
        </div>
        <div className="dl-pill dl-platform">
          <span>Email / DM</span>
        </div>
      </div>

      <div className="dl-pipe">
        <div className="dl-pipe-label">Droga konwersji od kliknięcia do kasy</div>
        <div className="dl-leaks">
          {leaks.map((x, i) => (
            <div className="dl-leak" key={i}>
              <div className="dl-leak-x">✕</div>
              <div className="dl-leak-text">
                <strong>{x.l}</strong>
                <span>{x.d}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dl-row dl-result">
        <div className="dl-box dl-box-fake">
          <div className="dl-box-label">Co widzisz w Ads Managerze</div>
          <div className="dl-box-num">~60%</div>
          <div className="dl-box-sub">sprzedaży / leadów</div>
          <div className="dl-box-meta">→ Twoje decyzje budżetowe</div>
        </div>
        <div className="dl-mismatch">
          <div className="dl-mismatch-x">≠</div>
          <div className="dl-mismatch-label">rozjazd 30–50%</div>
        </div>
        <div className="dl-box dl-box-real">
          <div className="dl-box-label">Co jest w CRM / Shopify</div>
          <div className="dl-box-num">100%</div>
          <div className="dl-box-sub">faktycznych transakcji</div>
          <div className="dl-box-meta">→ Twój wpływ na konto</div>
        </div>
      </div>

      <div className="dl-conclusion">
        Optymalizujesz na <strong>dane, których nie ma w realu</strong>. Skalujesz to, co świeci się
        w panelu — wyłączasz to, co realnie sprzedaje.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEAD MAGNET (email capture for low-intent cold traffic)
// ═══════════════════════════════════════════════════════════════════════════
function LeadMagnet() {
  if (!LEAD_WEBHOOK_URL) return null;
  return <LeadMagnetInner />;
}

function LeadMagnetInner() {
  const [emailVal, setEmailVal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailVal || !/^\S+@\S+\.\S+$/.test(emailVal)) {
      setError("Podaj poprawny email");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const sp = new URLSearchParams(window.location.search);
      const utms: Record<string, string> = {};
      for (const k of UTM_KEYS) {
        const v = sp.get(k);
        if (v) utms[k] = v;
      }
      const payload = {
        email: emailVal,
        page: "/lp/ads",
        magnet: "7-wyciekow-meta-ads-pl.pdf",
        ...utms,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || null,
      };
      const res = await fetch(LEAD_WEBHOOK_URL!, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });
      if (!res.ok && res.status !== 0) throw new Error("webhook " + res.status);
      trackConversion("Lead", {
        content_name: "lead-magnet-7-wyciekow",
        value: 0,
        currency: "PLN",
      });
      setDone(true);
    } catch (err) {
      setError("Coś poszło nie tak. Spróbuj ponownie albo wpadnij od razu do panelu.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="lead-magnet" id="magnet">
      <div className="container">
        <div className="lm-grid">
          <div className="lm-left">
            <span className="label">Darmowy materiał — 0 zł, 0 zobowiązań</span>
            <h2>
              PDF: <span className="hl">7 wycieków budżetu w Meta Ads</span>
              <br />
              które widzimy u 80% polskich biznesów.
            </h2>
            <p>
              Konkretne checklisty, screeny z prawdziwych kont (zanonimizowane), dokładne kroki
              naprawy. Czas na lekturę: 12 minut. Czas na fix: pierwszy wyciek zatkasz tego samego
              dnia.
            </p>
            <ul className="lm-list">
              <li>Jak Meta Ads Manager kłamie o ROAS — i jak to obejść w 5 minut</li>
              <li>3 ustawienia w CAPI które dowożą 25–40% ukrytych konwersji</li>
              <li>Lead Quality Scoring — dlaczego 30% Twoich leadów to śmieci dla Mety</li>
              <li>Atrybucja LTV vs last-click — dlaczego ucinasz najlepsze adsy</li>
              <li>
                +3 wycieki specyficzne dla polskiego e-commerce (Allegro / BaseLinker / Shopify)
              </li>
            </ul>
          </div>
          <div className="lm-right">
            {done ? (
              <div className="lm-success">
                <div className="lm-success-icon">✓</div>
                <h3>Wysłaliśmy go na maila.</h3>
                <p>Sprawdź skrzynkę (i SPAM, gdyby tam wpadło).</p>
                {LEAD_MAGNET_PDF_URL && (
                  <a
                    href={LEAD_MAGNET_PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ marginTop: 14 }}
                  >
                    Pobierz teraz →
                  </a>
                )}
                <a href={demoUrl()} className="btn btn-secondary" style={{ marginTop: 10 }}>
                  Wskakuj do panelu →
                </a>
              </div>
            ) : (
              <form className="lm-form" onSubmit={onSubmit}>
                <label htmlFor="lm-email">Twój email — tam wyślemy PDF</label>
                <input
                  id="lm-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="ty@firma.pl"
                  value={emailVal}
                  onChange={(e) => setEmailVal(e.target.value)}
                  disabled={submitting}
                />
                {error && <div className="lm-error">{error}</div>}
                <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                  {submitting ? "Wysyłam…" : "Wyślij mi PDF →"}
                </button>
                <div className="lm-meta">
                  Bez spamu. Bez "sprzedam Ci coś za 5 minut". 1 mail z linkiem, opt-out w stopce.
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MECHANISM (3-step how it works)
// ═══════════════════════════════════════════════════════════════════════════
function Mechanism() {
  return (
    <section className="mechanism" id="how-it-works">
      <div className="container">
        <div className="mechanism-header">
          <span className="label">Jak to działa</span>
          <h2>3 kroki. 8 minut. Pierwsze wnioski tego samego dnia.</h2>
          <p>
            Bez integracji przez deva. Bez kursów. Bez "porozmawiajmy o wdrożeniu". Logujesz Meta i
            Google → system robi resztę.
          </p>
        </div>
        <div className="steps">
          <div className="step">
            <div className="step-num">1</div>
            <h3>Podłącz konta</h3>
            <p>Meta Ads, GoHighLevel, własny CRM. OAuth — klikasz "połącz" i jedziesz.</p>
            <ul>
              <li>Server-side tracking (CAPI) auto-konfiguracja</li>
              <li>Mapowanie zdarzeń pod Twój lejek sprzedażowy</li>
            </ul>
          </div>
          <div className="step">
            <div className="step-num">2</div>
            <h3>Zobacz prawdę</h3>
            <p>
              Poprawne konwersje widzisz już od pierwszego leada po konfiguracji systemu. Zobaczysz
              całą ścieżkę klienta — czy pierwsze kliknięcie przyszło z Meta, Google Ads, czy z
              emaila — a system przypisze konwersję do odpowiedniej platformy.
            </p>
            <ul>
              <li>Atrybucja po LTV, nie po last-click</li>
              <li>Alerty Slack/email gdy CPA puchnie</li>
              <li>Raporty whitelabel dla klientów</li>
            </ul>
          </div>
          <div className="step">
            <div className="step-num">3</div>
            <h3>Generuj nowe kreacje</h3>
            <p>
              Grafiki z Twoim zdjęciem oraz video z Twoim zdjęciem lub wygenerowanym avatarem, który
              nie wygląda jak AI i mówi po polsku. Wszystko w stylu Twojej marki, gotowe do
              publikacji w 30 sekund.
            </p>
            <ul>
              <li>Grafiki w formatach pod Meta/TikTok/Allegro</li>
              <li>Video AI z Remotion + voiceover (PL)</li>
              <li>Avatar AI mówiący po polsku — nie wygląda jak AI</li>
            </ul>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <a href={demoUrl()} className="btn btn-primary btn-xl">
            Zacznij teraz →
          </a>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ICP QUALIFIER (polarize → boost qualified conversion, repel beginners)
// ═══════════════════════════════════════════════════════════════════════════
function ICPQualifier() {
  return (
    <section className="icp-qualifier" id="for-who">
      <div className="container">
        <div className="icp-grid">
          <div className="icp-col icp-yes">
            <div className="icp-head">
              <span className="icp-badge yes">✓ TAK, dla Ciebie</span>
              <h3>Jeśli to o Tobie:</h3>
            </div>
            <ul>
              <li>
                <strong>Pozyskujesz klientów online</strong> przez leady albo sklep e-commerce
              </li>
              <li>
                <strong>Lejesz min. 3 000 zł/m w Meta Ads</strong> i nie wiesz który ad realnie
                sprzedaje
              </li>
              <li>
                <strong>Tracisz godziny w Ads Managerze</strong> — testujesz kreacje, klikasz
                ustawienia, sklejasz raporty z Excela
              </li>
              <li>
                <strong>Chcesz wiedzieć realny koszt klienta</strong> — nie cyfrę z panelu, tylko to
                co siadło na koncie
              </li>
              <li>
                <strong>Potrzebujesz szybciej dostarczać kreacje</strong> (grafiki + video) pod
                testy reklamowe
              </li>
            </ul>
          </div>
          <div className="icp-col icp-no">
            <div className="icp-head">
              <span className="icp-badge no">✗ NIE, jeszcze nie teraz</span>
              <h3>Pomiń, jeśli:</h3>
            </div>
            <ul>
              <li>
                Wydajesz <strong>poniżej 500 zł/m</strong> na reklamy — VP się jeszcze nie zwróci
              </li>
              <li>
                Liczysz że z 200 zł budżetu dostaniesz <strong>ROAS 1000×</strong> — to nie magia,
                tylko narzędzie
              </li>
              <li>
                Nigdy nie odpaliłeś płatnej kampanii i szukasz <strong>kursu "od zera"</strong>
              </li>
              <li>
                Nie masz Meta Pixela ani sklepu / CRM-a —{" "}
                <strong>wróć po podstawowym setupie</strong>
              </li>
              <li>
                Szukasz "magicznego AI co zrobi mi sprzedaż" —{" "}
                <strong>VP to tool, nie agencja</strong>
              </li>
            </ul>
          </div>
        </div>
        <div className="icp-bottom">
          Kiwasz głową przy lewej kolumnie?{" "}
          <a href={demoUrl()} className="btn btn-primary icp-cta">
            Umów 30-min demo →
          </a>
          <div className="icp-bottom-sub">
            Pokażemy live na Twoim koncie reklamowym gdzie wycieka kasa.
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SEGMENT (Lead Gen vs Creators)
// ═══════════════════════════════════════════════════════════════════════════
function Segment() {
  return (
    <section className="segment" id="for-you">
      <div className="container">
        <div className="segment-header">
          <span className="label">Dla kogo</span>
          <h2>
            Zbudowaliśmy to dla biznesów które <span className="hl">żyją z reklam i contentu</span>.
          </h2>
          <p>
            Niezależnie czy puszczasz Meta Ads pod telefony i leady, czy skalujesz się organicznie
            contentem — wszystko w jednym koncie. Agencje, coachowie, B2B, SaaS, kursy online, real
            estate, e-commerce, twórcy, infoproduct, edu, agencje contentowe.
          </p>
        </div>
        <div className="seg-grid seg-grid-single">
          <div className="seg-card">
            <ul className="seg-merged-list">
              {[
                {
                  t: "Tracking jakości leadów + sprzedaży",
                  d: "który ad sprowadza zakwalifikowane calle / realny zakup, nie tylko klik",
                },
                { t: "Server-side CAPI", d: "Meta dostaje czyste dane → niższy CPA z czasem" },
                {
                  t: "Integracje CRM / e-com",
                  d: "GHL, HubSpot, Calendly, BaseLinker, Shopify — pełna ścieżka klienta",
                },
                {
                  t: "AI Marketing Analyst",
                  d: 'codzienne podsumowanie po polsku „co skalować, co ubić"',
                },
                {
                  t: "Lead Quality Scoring",
                  d: "automat oznacza śmieciowe leady, Meta uczy się dobrych",
                },
                { t: "Whitelabel raporty", d: "klient agencji dostaje swój panel z Twoim logo" },
                {
                  t: "Analityka contentu",
                  d: "które posty / reels / shortsy realnie sprzedają, nie tylko zbierają lajki",
                },
                {
                  t: "AI Grafiki (Ad / Carousel / Stories)",
                  d: "banery, karuzele, story w stylu Twojej marki",
                },
                {
                  t: "AI Video (UGC Studio + Reel Creator)",
                  d: "UGC-style video z avatarów + polski voiceover",
                },
                {
                  t: "YouTube Highlights",
                  d: "z 30-min filmu generuje 5–10 shortsów automatycznie",
                },
                {
                  t: "Idea Bank",
                  d: "bank pomysłów na content z Twojej niszy + transkrypty konkurencji",
                },
                {
                  t: "Content Scheduler",
                  d: "planuj i publikuj na IG / TikTok / YouTube / LinkedIn z jednego panelu",
                },
              ].map((f) => (
                <li key={f.t}>
                  <strong>{f.t}</strong>
                  <span>{f.d}</span>
                </li>
              ))}
            </ul>
            <a href={demoUrl()} className="btn btn-primary seg-cta">
              Umów demo →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURES GRID
// ═══════════════════════════════════════════════════════════════════════════
function Features() {
  const feats = [
    {
      i: "📊",
      t: "Cross-platform tracking",
      d: "Meta, Google, TikTok, Allegro w jednym dashboardzie. CAPI server-side. Łapie 30–50% sprzedaży niewidocznych w Ads Manager.",
    },
    {
      i: "🧠",
      t: "AI Marketing Analyst",
      d: "Codzienne podsumowanie po polsku: co skalować, co ubić, gdzie wycieka kasa. Bez tabelek do czytania.",
    },
    {
      i: "🎨",
      t: "Ad Creator (Grafiki)",
      d: "AI generuje banery i kreacje pod każdy format platformy. Brand voice, kolory, fonty Twojej marki.",
    },
    {
      i: "🎬",
      t: "Video Generator",
      d: "Video AI z Remotion + voiceover PL. Storytelling reels, UGC-style ads, animacje pod produkt.",
    },
    {
      i: "✍️",
      t: "AI Copywriter",
      d: "Headliny, CTA, opisy, warianty A/B w 30 sekund. Pod Twój ICP, w Twoim tonie.",
    },
    {
      i: "📱",
      t: "Stories + Reel Creator",
      d: "Story i Reels gotowe do publikacji. Trendujące hooki, transitions, captiony.",
    },
    {
      i: "🎭",
      t: "UGC Studio",
      d: "Video UGC z AI avatarów (HeyGen). PL voiceover. 30 sekund — gotowy creative do testu.",
    },
    {
      i: "🎠",
      t: "Carousel Creator",
      d: "Karuzele Instagram/LinkedIn. Edukacyjne, sprzedażowe, listy. AI zna co konwertuje w Twojej niszy.",
    },
    {
      i: "▶️",
      t: "YouTube Highlights",
      d: "Z 30-minutowej transmisji generuje 5–10 shortsów. Automatyczne cięcie, napisy, kadrowanie pionowe.",
    },
    {
      i: "💡",
      t: "Idea Bank",
      d: "Bank pomysłów na content + transkrypty konkurencji. Inspiracja → skrypt → publikacja w jednym narzędziu.",
    },
    {
      i: "📅",
      t: "Content Scheduler",
      d: "Planowanie i auto-publikacja na IG, TikTok, YouTube, LinkedIn, Allegro. Jeden kalendarz.",
    },
    {
      i: "💬",
      t: "Social Inbox",
      d: "Wiadomości, komentarze, DM-y z IG/FB/TikTok w jednym widoku. AI Replies pod Twój ton.",
    },
    {
      i: "🔗",
      t: "Integracje CRM",
      d: "GHL, HubSpot, BaseLinker, Shopify, Allegro, Calendly, EverWebinar, Ringostat, Slack.",
    },
    {
      i: "⚡",
      t: "Automatyzacje + n8n",
      d: "Webhooki, alerty, raporty cykliczne, własne automatyzacje. n8n natywnie wpięty.",
    },
    {
      i: "👥",
      t: "Multi-client / Agency",
      d: "Panel agencji, role, uprawnienia, whitelabel raporty, billing pod klienta. Skalujesz bez chaosu.",
    },
  ];
  return (
    <section className="features" id="features">
      <div className="container">
        <div className="features-header">
          <span className="label">15 modułów. Jedno konto.</span>
          <h2>Wszystko czego potrzebujesz pod ads + content.</h2>
          <p>
            Hyros + Looker Studio + Canva + ChatGPT + CapCut + Buffer + Zapier — w jednym panelu,
            mówiącym po polsku, za ułamek ceny.
          </p>
        </div>
        <div className="feat-grid">
          {feats.map((f) => (
            <div className="feat" key={f.t}>
              <div className="fi">{f.i}</div>
              <h4>{f.t}</h4>
              <p>{f.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURES INTRO — przed showcase-ami konkretnych funkcji
// ═══════════════════════════════════════════════════════════════════════════
function FeaturesIntro() {
  return (
    <section className="features-intro">
      <div className="container">
        <div className="fi-inner">
          <span className="label">Co dostajesz w środku</span>
          <h2>
            Najważniejsze funkcje Viral Partners <span className="hl">w akcji</span>.
          </h2>
          <p>
            Niżej kilka wybranych funkcji na realnych ekranach z aplikacji — żebyś wiedział co
            konkretnie kupujesz, zanim klikniesz "Umów demo".
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AD CREATIVES SHOWCASE (realne grafiki z Ad Creator-a)
// ═══════════════════════════════════════════════════════════════════════════
function AdCreativesShowcase() {
  return (
    <section className="ad-creatives" id="ad-creatives">
      <div className="container">
        <div className="feature-row">
          <div className="feature-row-left">
            <div className="feature-row-num">#1</div>
            <span className="label">Funkcja</span>
            <h2>
              AI Ad Creator — <span className="hl">grafiki reklamowe w 30 sek</span>.
            </h2>
            <p>
              Wpisujesz brief po polsku, AI generuje kreację w stylu Twojej marki — kolory, fonty,
              layouty, zdjęcia produktów. Format pod każdą platformę (feed 4:5, square 1:1, story
              9:16). Bez grafika, bez Canvy, bez Photoshopa.
            </p>
            <ul className="feature-row-bullets">
              <li>Brand voice i identyfikacja wizualna Twojej marki</li>
              <li>Eksport pod Meta / TikTok / Allegro / IG / Story</li>
              <li>Lead gen, e-commerce, usługi, nieruchomości — sprawdzone przykłady ↓</li>
            </ul>
          </div>
          <div className="feature-row-right">
            <div className="ad-creatives-grid">
              {AD_CREATIVES.map((c) => (
                <figure className="ad-creative" key={c.src}>
                  <img src={c.src} alt={c.alt} loading="lazy" />
                  <figcaption>
                    <span className="ad-creative-tag">{c.tag}</span>
                    <strong>{c.caption}</strong>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURE #2 — AI MARKETING ANALYST
// ═══════════════════════════════════════════════════════════════════════════
const ANALYST_SCREENSHOT =
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.35.56.png";

function AnalystShowcase() {
  return (
    <section className="ad-creatives feature-section" id="analyst-showcase">
      <div className="container">
        <div className="feature-row feature-row-reverse">
          <div className="feature-row-right">
            <figure className="ad-creative ad-creative-wide">
              <img
                src={ANALYST_SCREENSHOT}
                alt="AI Marketing Analyst — rekomendacje na żywym koncie reklamowym"
                loading="lazy"
              />
            </figure>
          </div>
          <div className="feature-row-left">
            <div className="feature-row-num">#2</div>
            <span className="label">Funkcja</span>
            <h2>
              AI Marketing Analyst —{" "}
              <span className="hl">rekomendacje + akcje na Twoim koncie</span>.
            </h2>
            <p>
              Po podłączeniu CRM-a / Shopify analityk dostaje pełen obraz: prawdziwe konwersje,
              sprzedaże i jakość leadów obok danych z Meta / Google / TikTok. Na bazie wiedzy z
              agencji która obsłużyła <strong>300+ klientów w różnych branżach</strong> wyciąga
              wnioski i daje gotowe rekomendacje po polsku.
            </p>
            <ul className="feature-row-bullets">
              <li>Co skalować, co wyłączyć, gdzie wycieka kasa — codziennie</li>
              <li>Rekomendacje oparte na CRM/Shopify, nie na zmyślonych danych z Ads Managera</li>
              <li>
                <strong>One-click action:</strong> klikasz przycisk w aplikacji → akcja wykonuje się
                na Twoim koncie reklamowym (zmień budżet, wyłącz adset, pauza reklamy)
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURE #3 — MONITORING & ALERTS
// ═══════════════════════════════════════════════════════════════════════════
const MONITORING_SCREENSHOT =
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.41.41.png";

function MonitoringShowcase() {
  return (
    <section className="ad-creatives feature-section" id="monitoring-showcase">
      <div className="container">
        <div className="feature-row">
          <div className="feature-row-left">
            <div className="feature-row-num">#3</div>
            <span className="label">Funkcja</span>
            <h2>
              Monitoring 24/7 — <span className="hl">koniec z 5× dziennie sprawdzaniem konta</span>.
            </h2>
            <p>
              Zamiast obsesyjnie odświeżać Ads Managera (i każde z kont, jeśli masz kilku klientów),
              ustawiasz monitoring raz. System pilnuje wyników w tle i pinguje Cię tylko jak coś
              faktycznie wymaga decyzji.
            </p>
            <ul className="feature-row-bullets">
              <li>
                <strong>Domyślne alerty</strong> gdy CPA / CPL / ROAS wychodzi poza próg — krytyczne
                i ostrzeżenia osobno
              </li>
              <li>
                <strong>Akcje 1 kliknięciem</strong> z poziomu alertu: wstrzymaj kampanię, adset lub
                reklamę
              </li>
              <li>
                <strong>Przeanalizuj AI</strong> — AI wyciąga przyczynę (źle skonfigurowane
                konwersje, wyczerpana grupa, drop CTR) i tłumaczy po polsku
              </li>
              <li>
                <strong>Slack integracja</strong> — te same powiadomienia lecą na wybrany kanał
              </li>
              <li>
                <strong>Custom monitoring</strong> — opisujesz po polsku co chcesz śledzić („pilnuj
                kampanii X gdy CPM &gt; 40 zł"), AI rozumie i monitoruje
              </li>
            </ul>
          </div>
          <div className="feature-row-right">
            <figure className="ad-creative ad-creative-wide">
              <img
                src={MONITORING_SCREENSHOT}
                alt="Monitoring konta reklamowego — alerty + akcje 1-click + AI analiza"
                loading="lazy"
              />
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURE #4 — QUICK CAMPAIGN (URL → ads)
// ═══════════════════════════════════════════════════════════════════════════
const QUICK_CAMPAIGN_SCREENSHOT =
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.47.33.png";

function QuickCampaignShowcase() {
  return (
    <section className="ad-creatives feature-section" id="quick-campaign-showcase">
      <div className="container">
        <div className="feature-row feature-row-reverse">
          <div className="feature-row-right">
            <figure className="ad-creative ad-creative-wide">
              {QUICK_CAMPAIGN_SCREENSHOT ? (
                <img
                  src={QUICK_CAMPAIGN_SCREENSHOT}
                  alt="Szybki tor kampanii — URL w 5 krokach do gotowych reklam"
                  loading="lazy"
                />
              ) : (
                <div
                  style={{
                    aspectRatio: "16/10",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--text-soft)",
                    fontSize: 13,
                  }}
                >
                  ⚠️ Wstaw URL grafiki w QUICK_CAMPAIGN_SCREENSHOT
                </div>
              )}
            </figure>
          </div>
          <div className="feature-row-left">
            <div className="feature-row-num">#4</div>
            <span className="label">Funkcja</span>
            <h2>
              Szybki tor kampanii — <span className="hl">URL → gotowe reklamy w 5 krokach</span>.
            </h2>
            <p>
              Wklejasz link do swojej strony / oferty. AI zasysa zawartość, generuje profil
              idealnego klienta, ściąga grafiki ze strony (albo robi własne naszym modelem), pisze
              copy, ustawia targeting i strukturę kampanii.{" "}
              <strong>Ty klikasz tylko „Akceptuj".</strong>
            </p>
            <ul className="feature-row-bullets">
              <li>Auto-analiza oferty + ICP w 30 sekund</li>
              <li>Grafiki: ze strony lub generowane naszym AI w stylu Twojej marki</li>
              <li>
                Targeting, budżet i struktura kampanii zaproponowane na podstawie 300+ obsłużonych
                klientów
              </li>
              <li>3 pytania zamiast 30 pól w Ads Managerze</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURE #5 — BULK UPLOAD
// ═══════════════════════════════════════════════════════════════════════════
const BULK_UPLOAD_SCREENSHOT =
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.49.39.png";

function BulkUploadShowcase() {
  return (
    <section className="ad-creatives feature-section" id="bulk-upload-showcase">
      <div className="container">
        <div className="feature-row">
          <div className="feature-row-left">
            <div className="feature-row-num">#5</div>
            <span className="label">Funkcja</span>
            <h2>
              Bulk upload —{" "}
              <span className="hl">25 reklam tygodniowo bez klikania w Ads Managera</span>.
            </h2>
            <p>
              Jeśli testujesz dużo kreatywów (np. 25 reklam tygodniowo na klienta), znasz ten flow:
              wrzucasz, wpisujesz primary text, headline, duplikujesz reklamę, podmieniasz
              video/grafikę, znowu duplikujesz, znowu podmieniasz… godziny klikania.
            </p>
            <p>
              <strong>
                U nas: przeciągasz tyle grafik i video ile chcesz, jeden raz wpisujesz teksty (albo
                generujesz wariant z AI) — i każdy plik staje się osobną reklamą z tymi samymi
                tekstami. Gotowe.
              </strong>
            </p>
            <ul className="feature-row-bullets">
              <li>Każdy plik = osobna reklama z identycznymi tekstami</li>
              <li>Do 5 wariantów Primary Text + Headline w jednym uploadzie</li>
              <li>JPG / PNG / WebP / MP4 / MOV — do 4 GB / plik</li>
              <li>AI uzupełnia warianty copy w 1 sekundę</li>
            </ul>
          </div>
          <div className="feature-row-right">
            <figure className="ad-creative ad-creative-wide">
              <img
                src={BULK_UPLOAD_SCREENSHOT}
                alt="Bulk upload reklam — drag & drop wielu plików, automatyczne tworzenie wariantów"
                loading="lazy"
              />
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FEATURE #6 — UGC VIDEO (Polish voiceover)
// ═══════════════════════════════════════════════════════════════════════════
function UGCVideoShowcase() {
  return (
    <section className="ad-creatives feature-section" id="ugc-video-showcase">
      <div className="container">
        <div className="feature-row feature-row-reverse">
          <div className="feature-row-right">
            <div className="ugc-video-frame">
              <iframe
                src="https://www.youtube.com/embed/qHO54Wvaeu8?rel=0&modestbranding=1"
                title="Przykład reklamy UGC z polskim głosem AI"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
          <div className="feature-row-left">
            <div className="feature-row-num">#6</div>
            <span className="label">Funkcja</span>
            <h2>
              UGC Video Creator — <span className="hl">reklamy z avatarami + polski głos AI</span>.
            </h2>
            <p>
              W naszym <strong>UGC Studio</strong> generujesz video z avatarów AI z naturalnym
              polskim głosem. Idealne pod reklamy aplikacji, lejków sprzedażowych, kursów online —
              wszędzie tam gdzie surowy „mówiący człowiek" konwertuje lepiej niż wypolerowana
              grafika.
            </p>
            <p>
              Po prawej: <strong>realny przykład</strong> — reklama lejka sprzedażowego wygenerowana
              przez nasze AI w kilka minut.
            </p>
            <ul className="feature-row-bullets">
              <li>Naturalny polski głos AI — nie brzmi jak Google Translator</li>
              <li>Biblioteka avatarów (różne typy, płcie, wiek) lub Twój własny avatar</li>
              <li>Skrypt generuje AI Copywriter na podstawie Twojego ICP i oferty</li>
              <li>Format pionowy 9:16 pod Reels / TikTok / Shorts / Story</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AD SPEND PROOF (real numbers from public stats endpoint)
// ═══════════════════════════════════════════════════════════════════════════
function AdSpendProof() {
  const { data } = useQuery<AdSpendStats>({
    queryKey: ["public-ad-spend-stats"],
    queryFn: async () => {
      const res = await fetch("/api/public/stats/ad-spend");
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    staleTime: 4 * 60 * 60 * 1000,
    retry: 1,
  });

  if (!data || data.entries.length === 0) return null;

  const top = data.entries.slice(0, TOP_AD_SPEND_COUNT);
  const sum = top.reduce((s, e) => s + e.totalSpend, 0);

  return (
    <section className="ad-spend-proof">
      <div className="container">
        <div className="ad-spend-box">
          <div className="as-label">🔥 Top {TOP_AD_SPEND_COUNT} użytkowników w tym miesiącu</div>
          <div className="as-entries">
            {top.map((e, i) => (
              <div className="as-entry" key={i}>
                <div className="as-rank">#{i + 1}</div>
                <div className="as-amount">{formatPln(e.totalSpend)}</div>
              </div>
            ))}
          </div>
          <div className="as-total">
            Tylko top 3 wydaje przez naszą platformę <strong>{formatPln(sum)}</strong> miesięcznie.
            Razem cała baza: {formatPln(data.totalManaged)}.
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════════════════════════════════
const TESTIMONIAL_SCREENSHOTS: string[] = [
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.56.20.png",
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.57.37.png",
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.58.03.png",
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.59.02.png",
  "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zrzut+ekranu+2026-05-6+o+20.59.12.png",
];

function Testimonials() {
  return (
    <section className="testimonials" id="proof">
      <div className="container">
        <div className="testi-header">
          <span className="label">Co mówią użytkownicy</span>
          <h2>Realne wiadomości od użytkowników.</h2>
        </div>
        <div className="testi-screens-grid">
          {TESTIMONIAL_SCREENSHOTS.map((src, i) => (
            <figure className="testi-screen" key={src}>
              <img src={src} alt={`Opinia użytkownika ${i + 1}`} loading="lazy" />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PO POLSKU (differentiator vs Hyros/Triple Whale)
// ═══════════════════════════════════════════════════════════════════════════
function PoPolsku() {
  return (
    <section className="po-polsku" id="po-polsku">
      <div className="container">
        <div className="pp-header">
          <span className="label">Czego nie zrobi Hyros, Triple Whale ani Northbeam</span>
          <h2>
            Cały panel po polsku.
            <br />Z polskim supportem. Z polskimi integracjami.
          </h2>
        </div>
        <div className="pp-grid">
          <div className="pp-card">
            <div className="pp-icon">🇵🇱</div>
            <h4>UI po polsku</h4>
            <p>
              Twoja księgowa otworzy raport i zrozumie. Twój junior nie potrzebuje kursu z
              angielskiego ad-jargonu.
            </p>
          </div>
          <div className="pp-card">
            <div className="pp-icon">💬</div>
            <h4>Polski support</h4>
            <p>
              Odpowiadamy przez Intercom — i to{" "}
              <strong>te same osoby, które na co dzień zawodowo prowadzą kampanie reklamowe</strong>{" "}
              dla naszych klientów. Nie support z Indii czytający z FAQ — realny marketer, który
              widzi dziesiątki kont tygodniowo i wie co działa.
            </p>
          </div>
          <div className="pp-card">
            <div className="pp-icon">📊</div>
            <h4>Faktura VAT, PLN</h4>
            <p>
              Płacisz w PLN, dostajesz fakturę VAT. Nie męczysz się z USD przewalutowanym przez
              Stripe co miesiąc po innym kursie.
            </p>
          </div>
          <div className="pp-card">
            <div className="pp-icon">🎬</div>
            <h4>AI po polsku</h4>
            <p>
              Copywriter, lektor w UGC Studio, transkrypty YouTube — wszystko płynnie po polsku. Nie
              „polski przetłumaczony przez ChatGPT".
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FOUNDER NOTE (humanize → trust)
// ═══════════════════════════════════════════════════════════════════════════
function FounderNote() {
  return (
    <section className="founder-note">
      <div className="container-tight">
        <div className="founder-card">
          <div className="founder-avatar">M</div>
          <div className="founder-body">
            <div className="founder-meta">
              <strong>Maciej, founder Viral Partners</strong>
              <span>Polska · 6 lat w performance marketingu</span>
            </div>
            <p>
              Zbudowaliśmy VP, bo sami płaciliśmy 8000 zł miesięcznie za stack narzędzi które się
              nie gadały. Spędzaliśmy 3 dni w tygodniu na klejeniu raportów w Excelu zamiast
              skalować klientów. Jeśli też tak masz — wejdź, zobacz panel, zadaj mi dowolne pytanie
              na czacie. Odpisuję osobiście do 4h w dni robocze.
            </p>
            <p style={{ marginTop: 10, fontSize: 14 }}>
              Nie mamy działu sprzedaży. Mamy panel który mówi sam za siebie. Wejdź, zobacz panel —
              jak nie zadziała, po prostu odejdziesz.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GUARANTEE
// ═══════════════════════════════════════════════════════════════════════════
function Guarantee() {
  return (
    <section className="guarantee" id="guarantee">
      <div className="container">
        <div className="guarantee-box">
          <div className="guarantee-seal">
            ZERO
            <br />
            UMÓW
          </div>
          <div>
            <h3>Anuluj kiedy chcesz. Bez umów. Bez pułapek.</h3>
            <p>
              Płacisz miesiąc do miesiąca. Jeśli VP nie oszczędza Ci kasy i czasu — wyłączasz
              subskrypcję jednym klikiem w ustawieniach. Bez kary, bez "powiedz nam dlaczego", bez
              chowanego przycisku. Zostajesz <strong>tylko dopóki sam tego chcesz.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRICING TEASER
// ═══════════════════════════════════════════════════════════════════════════
function PricingTeaser() {
  return (
    <section className="pricing-teaser" id="pricing">
      <div className="container">
        <div className="pricing-box">
          <div className="pt-pre">Wszystko w jednym</div>
          <div className="pt-price">
            2 349 zł<small> /miesiąc</small>
          </div>
          <div className="pt-vs">
            zamiast <s>~9 800 zł/m</s> za 7 narzędzi osobno
          </div>
          <div className="pt-stack">
            <div className="pt-stack-row">
              <span>Hyros (tracking + atrybucja)</span>
              <strong>2 700 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>Looker Studio konsultant + raporty</span>
              <strong>1 800 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>Canva Pro + Adobe (grafiki)</span>
              <strong>320 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>ChatGPT Plus / Claude Pro (copy)</span>
              <strong>180 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>CapCut Pro / edytor video</span>
              <strong>2 000 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>Buffer / Hootsuite (scheduler)</span>
              <strong>400 zł/m</strong>
            </div>
            <div className="pt-stack-row">
              <span>Zapier / n8n + integracje</span>
              <strong>800 zł/m</strong>
            </div>
            <div className="pt-stack-row pt-stack-sum">
              <span>Razem typowy stack</span>
              <strong>~8 200 zł/m</strong>
            </div>
            <div className="pt-stack-row pt-stack-vp">
              <span>Viral Partners (wszystko)</span>
              <strong>2 349 zł/m</strong>
            </div>
          </div>
          <ul>
            <li>Pełen tracking (Meta, Google, TikTok, Allegro, Shopify)</li>
            <li>15 modułów AI (grafiki, video, copy, scheduler, analyst)</li>
            <li>Wszystkie integracje + whitelabel raporty</li>
            <li>Support po polsku</li>
            <li>Bez limitu kont reklamowych w jednym workspace</li>
          </ul>
          <div className="pt-vs-table">
            <div className="pt-vs-row pt-vs-head">
              <span></span>
              <span>Hyros</span>
              <span>Triple Whale</span>
              <span className="pt-vs-vp">Viral Partners</span>
            </div>
            <div className="pt-vs-row">
              <span>Tracking + CAPI</span>
              <span>✓</span>
              <span>✓</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row">
              <span>AI grafiki + video</span>
              <span>—</span>
              <span>—</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row">
              <span>Content scheduler</span>
              <span>—</span>
              <span>—</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row">
              <span>Whitelabel agency</span>
              <span>—</span>
              <span>partial</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row">
              <span>Allegro / BaseLinker</span>
              <span>—</span>
              <span>—</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row">
              <span>UI po polsku</span>
              <span>—</span>
              <span>—</span>
              <span className="pt-vs-vp">✓</span>
            </div>
            <div className="pt-vs-row pt-vs-price">
              <span>Cena/m</span>
              <span>~2 700 zł</span>
              <span>~3 200 zł</span>
              <span className="pt-vs-vp">2 349 zł</span>
            </div>
          </div>
          <div className="pt-cta">
            <a href={demoUrl()} className="btn btn-primary btn-xl">
              Zacznij teraz
            </a>
            <a href="https://viral.partners/cennik" className="btn btn-secondary btn-xl">
              Zobacz pełen cennik
            </a>
          </div>
          <div className="pt-meta">Anuluj kiedy chcesz · Bez umów · Polski support</div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════════════════════════════════════
function FAQ() {
  const tokenAnswer = (
    <div className="faq-tokens">
      <p>
        <strong>Tokeny zużywają tylko funkcje generatywne AI.</strong> Reszta działa bez limitu.
      </p>

      <div className="faq-tokens-section">
        <div className="faq-tokens-title">Bez tokenów (unlimited):</div>
        <ul className="faq-tokens-unlimited">
          <li>Tracking + dashboardy</li>
          <li>Scheduler postów (IG / TikTok / YouTube / LinkedIn)</li>
          <li>Integracje CRM, sklepów i platform reklamowych</li>
          <li>Monitoring AI i alerty</li>
          <li>Panel agencyjny + whitelabel raporty</li>
          <li>CAPI</li>
        </ul>
        <p className="faq-tokens-note">
          Każde <strong>kliknięcie Analityka AI</strong> (rekomendacja / akcja na koncie) pobiera
          tokeny.
        </p>
      </div>

      <div className="faq-tokens-section">
        <div className="faq-tokens-title">Co dostajesz miesięcznie:</div>
        <table className="faq-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Tokeny</th>
              <th>≈ grafik</th>
              <th>≈ rolek</th>
              <th>≈ kliknięć Analityka AI</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Start</td>
              <td>2 000</td>
              <td>40</td>
              <td>28</td>
              <td>28</td>
            </tr>
            <tr>
              <td>Pro</td>
              <td>6 000</td>
              <td>120</td>
              <td>85</td>
              <td>85</td>
            </tr>
            <tr>
              <td>Expert</td>
              <td>18 000</td>
              <td>360</td>
              <td>257</td>
              <td>257</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="faq-tokens-foot">
        Tokeny kumulują się w cyklu rozliczeniowym. Zużyłeś więcej? Dokupujesz paczkę bez zmiany
        planu.
      </p>
    </div>
  );

  const faqs: { q: string; a: React.ReactNode }[] = [
    {
      q: "Na ile starczą mi tokeny AI w danym planie?",
      a: tokenAnswer,
    },
    {
      q: "Czy muszę wpinać kod na stronę? Mam dewelopera tylko raz w tygodniu.",
      a: 'Nie. Większość integracji to OAuth (klikasz "połącz" — np. Meta, Google, Shopify, Allegro). CAPI i tracking konfigurujemy server-side. W 90% przypadków zero pracy z developerem. Jeśli masz nietypowy stack — pomoc tech wpisana w plan.',
    },
    {
      q: "Czym to się różni od Hyrosa?",
      a: "Hyros to sam tracker (świetny, drogi, po angielsku). Viral Partners to cały system: tracking + AI-owe kreacje (grafiki, video, teksty) + planer postów + analityk AI + raporty whitelabel + integracje z CRM-ami. Robimy to, co Hyros, plus jeszcze kilkanaście rzeczy — w jednym panelu, po polsku, taniej.",
    },
    {
      q: "Mam małą agencję — sprawdzi się pod 5 klientów?",
      a: "Tak — to nasz najsilniejszy use case. Multi-client panel, role, uprawnienia, raporty pod klienta, oddzielny billing. Skalujesz portfolio bez Excela do śledzenia kto co ma.",
    },
    {
      q: "Czy kreacje z AI nie wyglądają jak generyczne fotki ze stocku?",
      a: 'Nie. Trenujemy generator na identyfikacji Twojej marki — kolory, fonty, ton, zdjęcia produktów, układy z Twoich wcześniejszych kreacji. Efekt to „moje grafiki, tylko 30× szybciej", a nie sztampa wypluta przez ChatGPT.',
    },
    {
      q: "Wydajemy 1000 zł+ na adsy, czy to się już zacznie opłacać u nas?",
      a: "Tak. Próg opłacalności trafiamy już przy ~1000 zł/m wydatków na ads — bo VP zastępuje 4–5 narzędzi (~1500–2000 zł/m oszczędności na samych subskrypcjach), plus tracking podnosi ROAS średnio 15–25%. Przy Twoim budżecie sam efekt na ROAS spina koszt narzędzia z nawiązką.",
    },
    {
      q: "Jak długi onboarding?",
      a: "8–15 minut sam (większość userów). Plan Pro — 30 min calla z naszym zespołem, my robimy konfigurację, Ty patrzysz. Pierwsze raporty masz tego samego dnia.",
    },
    {
      q: "Mogę anulować w każdej chwili?",
      a: 'Tak. Bez kary, bez "powiedz nam dlaczego", bez chowanego przycisku. W ustawieniach jeden klik.',
    },
    {
      q: "Czy moje dane są bezpieczne?",
      a: "Hosting w UE (Polska/Niemcy). Dane szyfrowane at-rest i in-transit. RODO compliant. Meta App Review przeszliśmy. Nie sprzedajemy danych — jesteśmy SaaS-em na subskrypcji, to konflikt interesów i tego się nie robi.",
    },
  ];
  return (
    <section className="faq" id="faq">
      <div className="container-tight">
        <div className="faq-header">
          <span className="label">Najczęstsze pytania</span>
          <h2>Co ludzie pytają zanim zaczną.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <details className="faq-item" key={i}>
              <summary>{f.q}</summary>
              <div className="faq-body">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DEMO PREVIEW (what you get on the call) — boosts booked-call conversion
// ═══════════════════════════════════════════════════════════════════════════
function SlotsScarcity() {
  const total = 8;
  const [free, setFree] = useState<number>(2);
  useEffect(() => {
    const id = setInterval(() => setFree((f) => (f === 2 ? 1 : 2)), 2200);
    return () => clearInterval(id);
  }, []);
  const taken = total - 2;
  return (
    <div className="slots-scarcity">
      <div className="slots-headline">
        ⚡ Pospiesz się — jesteśmy w stanie wdrożyć i skonfigurować system{" "}
        <strong>dla 5 nowych klientów</strong> do końca tygodnia
      </div>
      <div className="slots-bar">
        {Array.from({ length: total }).map((_, i) => {
          const isFreeSlot = i >= taken;
          const isPenultimate = i === total - 2;
          const visibleAsFree = isFreeSlot && (!isPenultimate || free === 2);
          return (
            <div key={i} className={visibleAsFree ? "slot slot-free" : "slot slot-taken"}>
              {visibleAsFree && (
                <span className={isPenultimate ? "slot-check slot-check-pulse" : "slot-check"}>
                  ✓
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DemoPreview() {
  useEffect(() => {
    const SCRIPT_ID = "ghl-form-embed-script";
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "https://api.whitepalmgroup.com/js/form_embed.js";
    s.type = "text/javascript";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <section className="demo-preview" id="demo">
      <div className="container">
        <div className="demo-grid">
          <div>
            <span className="label">Wolisz pogadać niż klikać?</span>
            <h2>30-minutowe demo. Zero slajdów sprzedażowych.</h2>
            <p className="demo-sub">
              Pokazujemy live na Twoim koncie reklamowym, gdzie konkretnie wycieka kasa i co byśmy
              od razu zrobili. Wychodzisz z calla z action listą — niezależnie czy zostajesz, czy
              nie.
            </p>
            <ul className="demo-list">
              <li>
                <strong>Mini-audyt Twoich Meta/Google adsów</strong> — pokażemy 2–3 największe
                wycieki w pierwszych 8 minutach
              </li>
              <li>
                <strong>Personalizowany setup</strong> — które moduły VP są najważniejsze dla
                Twojego modelu (lead gen / ecom / content / agencja)
              </li>
              <li>
                <strong>Realna prognoza ROI</strong> — ile możesz odzyskać z budżetu i ile czasu
                oszczędzisz, na liczbach
              </li>
              <li>
                <strong>Q&amp;A bez ciśnienia</strong> — pytasz, my odpowiadamy. Nie ma "zaraz
                przekażę Ci ofertę" — wszystko widzisz tu i teraz
              </li>
            </ul>
            <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 12 }}>
              Zoom · 30 min · Po polsku · Nie sprzedajemy na sile
            </div>
          </div>
          <div className="demo-card demo-card-calendar">
            <SlotsScarcity />
            <iframe
              src="https://api.whitepalmgroup.com/widget/booking/ea55BM0B5SkCS4vDrIQG"
              style={{ width: "100%", border: "none", overflow: "hidden", minHeight: 720 }}
              scrolling="no"
              id="ea55BM0B5SkCS4vDrIQG_1778095032995"
              title="Umów rozmowę"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FINAL CTA
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// PRICING TIERS (3-plan pricing matrix)
// ═══════════════════════════════════════════════════════════════════════════
function registerWithPlanUrl(plan: "starter" | "pro"): string {
  const passthrough = passthroughParams();
  const sep = passthrough ? "&" : "?";
  return `https://app.viral.partners/checkout${passthrough}${sep}plan=${plan}&cycle=monthly`;
}

function PricingTiers() {
  return (
    <section className="pricing-tiers" id="pricing-tiers">
      <div className="container">
        <div className="tiers-header">
          <h2>
            Dołącz już <span className="hl">teraz!</span>
          </h2>
        </div>
        <div className="tiers-grid">
          {/* TIER 1 — Start */}
          <div className="tier-card">
            <h3>Start</h3>
            <p className="tier-sub">Cała maszyna do tworzenia reklam i contentu</p>
            <div className="tier-price">
              <strong>499 zł</strong>
              <small>/msc</small>
              <div className="tier-price-meta">netto + 23% VAT · 1 użytkownik</div>
            </div>
            <ul className="tier-list">
              <li>Twórca reklam graficznych</li>
              <li>Twórca reklam video</li>
              <li>Twórca skryptów reklam</li>
              <li>Twórca VSL</li>
              <li>Twórca filmów na YouTube</li>
              <li>Twórca rolek (IG / TikTok / Shorts)</li>
              <li>Twórca strategii marketingowej</li>
              <li>Kreator kampanii reklamowych z AI</li>
              <li>Analityk AI</li>
              <li>Monitoring AI</li>
              <li>Wsparcie Intercom</li>
            </ul>
            <a href={registerWithPlanUrl("starter")} className="btn btn-secondary btn-xl tier-cta">
              Zacznij teraz
            </a>
          </div>

          {/* TIER 2 — Pro (Popular) */}
          <div className="tier-card tier-card-popular">
            <span className="tier-badge">Bestseller</span>
            <h3>Pro</h3>
            <p className="tier-sub">Wszystko ze Start + integracja z CRM</p>
            <div className="tier-price">
              <strong>1 499 zł</strong>
              <small>/msc</small>
              <div className="tier-price-meta">netto + 23% VAT · 3 użytkowników</div>
            </div>
            <ul className="tier-list">
              <li>
                <strong>Wszystkie funkcje z Planu Start</strong>
              </li>
              <li>Połączenie z CRM (LeadConnector / HubSpot / Calendly)</li>
              <li>Połączenie ze sklepem e-commerce (Shopify / BaseLinker)</li>
              <li>Priorytetowe wsparcie Intercom</li>
            </ul>
            <a href={registerWithPlanUrl("pro")} className="btn btn-primary btn-xl tier-cta">
              Wybieram ten pakiet
            </a>
          </div>

          {/* TIER 3 — Expert */}
          <div className="tier-card">
            <h3>Expert</h3>
            <p className="tier-sub">Wszystko z Pro + pełna ścieżka klienta</p>
            <div className="tier-price">
              <strong>5 999 zł</strong>
              <small>/msc</small>
              <div className="tier-price-meta">netto + 23% VAT · 10 użytkowników</div>
            </div>
            <ul className="tier-list">
              <li>
                <strong>Wszystkie funkcje z Planu Pro</strong>
              </li>
              <li>Server-side CAPI (Meta)</li>
              <li>Advanced tracking — pokazuje pełną ścieżkę klienta od kliknięcia do kasy</li>
              <li>Panel agencyjny (whitelabel)</li>
              <li>Dedykowany opiekun konta</li>
              <li>Priorytetowe wsparcie Intercom z SLA</li>
            </ul>
            <a href="#demo" className="btn btn-secondary btn-xl tier-cta">
              Umów się na rozmowę
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="final-cta">
      <div className="container">
        <span className="label">Dziś, nie w przyszłym kwartale</span>
        <h2>
          Przestań optymalizować na <span className="hl">zmyślone dane</span>.<br />
          Zobacz prawdę o swoich reklamach — w 8 minut.
        </h2>
        <p>
          Pełen dostęp od razu. Bez "porozmawiajmy o wdrożeniu", bez długich umów. Logujesz się,
          podpinasz Meta i Google, pijesz kawę, masz pierwszy raport.
        </p>
        <div className="ctas">
          <a href="#pricing-tiers" className="btn btn-primary btn-xl">
            Dołącz już teraz →
          </a>
          <a href={demoUrl()} className="btn btn-secondary btn-xl">
            Pokaż mi audyt mojego konta (30 min)
          </a>
        </div>
        <div className="meta">Setup 8 minut · Anuluj kiedy chcesz · Bez umów</div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STICKY MOBILE CTA
// ═══════════════════════════════════════════════════════════════════════════
function StickyCTA() {
  return (
    <div className="sticky-cta">
      <a href={demoUrl()} className="btn btn-primary btn-block">
        Zacznij teraz →
      </a>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════════════════
function Footer() {
  return (
    <footer className="sl-footer">
      <div className="container">
        <div className="copy">© 2026 Viral Partners · VP Holding PSA</div>
        <div className="links">
          <a href="https://viral.partners/regulamin">Regulamin</a>
          <a href="https://viral.partners/polityka-prywatnosci">Polityka prywatności</a>
          <a href="https://viral.partners/cennik">Cennik</a>
          <a href="mailto:maciej@viral.partners">Kontakt</a>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function SalesPage() {
  useEffect(() => {
    if (window.fpr) window.fpr("click");
    if (META_PIXEL_ID) injectMetaPixel(META_PIXEL_ID);
    if (GA4_ID) injectGA4(GA4_ID);
    if (TIKTOK_PIXEL_ID) injectTikTokPixel(TIKTOK_PIXEL_ID);
    trackConversion("ViewContent", { content_name: "sales-page-ads", content_category: "landing" });
  }, []);

  // SEO + OG meta injection (helps Googlebot + tab title; does NOT help static FB sharing — needs SSR)
  useEffect(() => {
    const origTitle = document.title;
    document.title = SEO_CONFIG.title;

    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        const [_, attrName, attrVal] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
        if (attrName && attrVal) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    setMeta('meta[name="description"]', "content", SEO_CONFIG.description);
    setMeta('meta[property="og:title"]', "content", SEO_CONFIG.title);
    setMeta('meta[property="og:description"]', "content", SEO_CONFIG.description);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:url"]', "content", SEO_CONFIG.canonical);
    setMeta('meta[property="og:image"]', "content", SEO_CONFIG.ogImage);
    setMeta('meta[property="og:image:width"]', "content", "1200");
    setMeta('meta[property="og:image:height"]', "content", "630");
    setMeta('meta[property="og:locale"]', "content", "pl_PL");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", SEO_CONFIG.title);
    setMeta('meta[name="twitter:description"]', "content", SEO_CONFIG.description);
    setMeta('meta[name="twitter:image"]', "content", SEO_CONFIG.ogImage);
    setLink("canonical", SEO_CONFIG.canonical);

    return () => {
      document.title = origTitle;
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("allow-scroll");
    return () => {
      document.documentElement.classList.remove("allow-scroll");
    };
  }, []);

  // Scroll-triggered fade-in for sections
  useEffect(() => {
    const targets = document.querySelectorAll(".sales-landing section");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  const onAnchorClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const a = target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") ?? "";

    // Smooth-scroll for anchors. The "Umów demo" CTAs all use href="#demo"
    // and land on the in-page GHL booking iframe, so we still fire Schedule/Lead
    // conversion events here (used to be on the calendly.com outbound click).
    if (href.startsWith("#")) {
      e.preventDefault();
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (href === "#demo") {
        trackConversion("Schedule", { content_name: "demo-cta" });
        trackConversion("Lead", { content_name: "demo-cta", value: 0, currency: "PLN" });
      }
      return;
    }

    // Conversion event firing on outbound CTA clicks
    const cls = a.className || "";
    const isCTA =
      cls.includes("btn-primary") || cls.includes("sl-nav-cta") || cls.includes("seg-cta");
    if (!isCTA) return;

    if (href.includes("/register") || href.includes("app.viral.partners")) {
      // Trial CTA — fire Lead + InitiateCheckout (covers both Meta optimization choices)
      trackConversion("Lead", { content_name: "trial-cta", value: 0, currency: "PLN" });
      trackConversion("InitiateCheckout", { content_name: "trial-cta", value: 0, currency: "PLN" });
    }
  }, []);

  return (
    <div className="sales-landing" onClick={onAnchorClick}>
      <UrgencyStrip />
      <Hero />
      <Problem />
      <LeadMagnet />
      <Mechanism />
      <ICPQualifier />
      <FeaturesIntro />
      <AdCreativesShowcase />
      <AnalystShowcase />
      <MonitoringShowcase />
      <QuickCampaignShowcase />
      <BulkUploadShowcase />
      <UGCVideoShowcase />
      <AdSpendProof />
      <Testimonials />
      <PoPolsku />
      <DemoPreview />
      <PricingTiers />
      <FinalCTA />
      <FAQ />
      <Footer />
      <StickyCTA />
    </div>
  );
}
